import type { IFinancialsTemplatesQueryVariables } from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import {
  useOwnTemplates,
  usePublicTemplates,
  useStarredTemplates,
} from "components/financials/modern/templates/hooks/hooks";
import useSelfie from "lib/hooks/useSelfie";
import { IFinancialsTemplateNamespace, IFinancialsTemplatesQueryOrderField, IOrderDirection } from "types/graphqlTypes";

export type UseTemplatesReturnType = {
  ownTemplates: ReturnType<typeof useOwnTemplates>;
  starredTemplates: ReturnType<typeof useStarredTemplates>;
  publicTemplates: ReturnType<typeof usePublicTemplates>;
  ourTemplates: {
    templates: ReturnType<typeof usePublicTemplates>["templates"];
  };
};

export default function useTemplates(variables: IFinancialsTemplatesQueryVariables): UseTemplatesReturnType {
  const viewer = useSelfie();

  const {
    templates,
    breakdown,
    loading,
    refetch: refetchOwnTemplates,
  } = useOwnTemplates({
    ...variables,
    filters: {
      ...variables.filters,
      filterByNamespace: [IFinancialsTemplateNamespace.User],
      filterByViewerId: viewer?._id,
      filterByViewerArchivedTemplates: true,
    },
  });
  const {
    templates: starredTemplates,
    loading: starredTemplatesLoading,
    refetch: refetchStarredTemplates,
    totalCount: starredTemplatesTotalCount,
  } = useStarredTemplates({
    filters: {
      ...variables.filters,
      filterByNamespace: null,
    },
    orderBy: null,
    limit: 30,
    cursor: null,
  });

  const {
    templates: publicTemplates,
    loading: publicTemplatesLoading,
    refetch: refetchPublicTemplates,
    fetchMore: fetchMorePublicTemplates,
    variables: publicTemplatesVariables,
    pageInfo: publicTemplatesPageInfo,
    networkStatus: publicTemplatesNetworkStatus,
    totalCount: publicTemplatesTotalCount,
  } = usePublicTemplates(variables);

  const { templates: ourTemplates } = usePublicTemplates({
    filters: {
      ...variables.filters,
      filterByNamespace: [IFinancialsTemplateNamespace.Fundamentei],
    },
    orderBy: {
      field: IFinancialsTemplatesQueryOrderField.UseCount,
      direction: IOrderDirection.Descending,
    },
    limit: 5,
    cursor: null,
  });

  return {
    ownTemplates: {
      templates,
      breakdown,
      loading,
      refetch: refetchOwnTemplates,
    },
    starredTemplates: {
      templates: starredTemplates,
      loading: starredTemplatesLoading,
      refetch: refetchStarredTemplates,
      totalCount: starredTemplatesTotalCount,
    },
    publicTemplates: {
      templates: publicTemplates,
      loading: publicTemplatesLoading,
      refetch: refetchPublicTemplates,
      fetchMore: fetchMorePublicTemplates,
      variables: publicTemplatesVariables,
      pageInfo: publicTemplatesPageInfo,
      networkStatus: publicTemplatesNetworkStatus,
      totalCount: publicTemplatesTotalCount,
    },
    ourTemplates: {
      templates: ourTemplates,
    },
  };
}
